import { EditorState, Plugin, Transaction } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import { Schema, DOMParser, Node, DOMSerializer } from 'prosemirror-model';
import { keymap } from 'prosemirror-keymap';
import { baseKeymap } from "prosemirror-commands"
import { PeekyEmitter } from '@shared/peeky.emitter';
import { nodes, marks } from './schema';
import { buildKeymap } from './keymap';

import { defaultMarkdownSerializer } from 'prosemirror-markdown';
import { getMarkdownParser } from './markdownParser';

import { createTransaction } from /* webpackChunkName: "create-transaction" */'./create-transaction';


type TagMap = { [key: string]: boolean };


function getSchema(tagMap: TagMap ): Schema {
  let enabledNodes;
  let enabledMarks;
  let inline = true;

  if (!tagMap) {
    enabledNodes = nodes;
    enabledMarks = marks;
    inline = false;
  } else {
    enabledNodes = {};
    enabledMarks = {};

    for (let key in tagMap) {
      if (nodes[key]) {
        enabledNodes[key] = nodes[key];
        if (nodes[key].group === 'block' || nodes[key].type === 'block') {
          inline = false;
        }
      }
      if (marks[key]) {
        enabledMarks[key] = marks[key];
      }
    }

    if (inline) {
      enabledNodes.doc = nodes.inlineDoc;
    } else {
      enabledNodes.doc = nodes.doc;
    }
    enabledNodes.text = nodes.text;
  }

  return new Schema({
    nodes: enabledNodes,
    marks: enabledMarks
  });
}


export class TextEditor extends PeekyEmitter {

  _view: EditorView;

  constructor(private _dom: HTMLElement, tagMap?: TagMap) {
    super();

    let schema = getSchema(tagMap);
    let texteditor = this;
    let parser = DOMParser.fromSchema(schema);
    let state = EditorState.create({
      doc: parser.parse(_dom),
      schema,
      plugins: [
        new Plugin({
          view: (view: EditorView) => {
            return {
              update: function(a, b) {
                texteditor.trigger('change');
              }
            }
          }
        }),
        keymap(baseKeymap),
        keymap(buildKeymap(schema))
      ]
    });
    this._view = new EditorView({
      mount: _dom
    }, {
      state
    });
    _dom.setAttribute('spellcheck', 'false');
    _dom.style.whiteSpace = 'pre-wrap';
  }


  toHtml() {
    return this._dom.innerHTML;
  }


  toMarkdown() {
    return defaultMarkdownSerializer.serialize(this._view.state.doc);
  }


  fromHtml(html: string) {
    let view = this._view;
    let state = view.state;
    let schema = state.schema;
    let newElement = document.createElement('div');
    newElement.innerHTML = html;
    let newDoc = DOMParser.fromSchema(schema).parse(newElement); 

    return this.fromNode(newDoc);
  }

  
  fromNode(node: Node) {
    let view = this._view;
    let state = view.state;
    try {
      let transaction = createTransaction(
        state,
        node
      );

      if (transaction.steps.length > 0) {
        let newState = state.apply(transaction);
        view.updateState(newState);
      }
    } catch(e) {
    }
  }


  fromMarkdown(markdown: string) {
    //let markdownParser = new MarkdownParser(this._view.state.schema);
    let markdownParser = getMarkdownParser(this._view.state.schema);
    let newDoc = markdownParser.parse(markdown);
    this.fromNode(newDoc);
  }

}